// Reports hub — React port of reports/ReportsComponent.vue: entity totals from
// GET reports/totals plus links to every report page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { ChartSquare, StatusUp } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage } from '../api/client';
import { isSuperUser } from '../auth/permissions';
import { useAuth } from '../components/AuthGate';
import { t } from '../i18n';
import { onLocationChanged } from '../session/location';
import { reportConfigs } from '../features/reports/reportConfigs';
import { num } from '../features/reports/reportUtils';
import { UI } from '../brand/ui';

// Same order as the legacy prepareData(totals).
const TOTALS: Array<{ key: string; label: string }> = [
  { key: 'sales', label: 'report.totals.sales' },
  { key: 'purchases', label: 'report.totals.purchases' },
  { key: 'incomes', label: 'report.totals.incomes' },
  { key: 'expenses', label: 'report.totals.expenses' },
  { key: 'quotations', label: 'report.totals.quotations' },
  { key: 'return_orders', label: 'report.totals.returnOrders' },
  { key: 'stock_adjustments', label: 'report.totals.stockAdjustments' },
  { key: 'stock_transfers', label: 'report.totals.stockTransfers' },
  { key: 'customers', label: 'report.totals.customers' },
  { key: 'suppliers', label: 'report.totals.suppliers' },
  { key: 'categories', label: 'report.totals.categories' },
  { key: 'brands', label: 'report.totals.brands' },
  { key: 'standard_items', label: 'report.totals.items' },
  { key: 'services', label: 'report.totals.services' },
  { key: 'recipes', label: 'report.totals.recipes' },
  { key: 'combos', label: 'report.totals.combos' }
];

export function ReportsPage() {
  const { user } = useAuth();
  const [totals, setTotals] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => onLocationChanged(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    let alive = true;
    setError(null);
    adminApi
      .get('reports/totals')
      .then((res) => alive && setTotals(((res.data as Record<string, unknown>)?.totals as Record<string, unknown>) ?? {}))
      .catch((err) => {
        if (!alive) return;
        setTotals({});
        setError(apiErrorMessage(err, t('report.unableToLoad')));
      });
    return () => {
      alive = false;
    };
  }, [tick]);

  const reports = reportConfigs.filter((report) => !report.superOnly || isSuperUser(user));

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('nav.reports')}
        </Typography>
        <Typography color="text.secondary">{t('report.hub.desc')}</Typography>
      </Box>

      {error ? <Alert severity="warning">{error}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {TOTALS.map((entry) => (
          <Card key={entry.key}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {totals ? Math.trunc(num(totals[entry.key])).toLocaleString() : <Skeleton width={60} />}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {t(entry.label)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ChartSquare size={24} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('report.detailedReports')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('report.detailedReports.desc')}
        </Typography>
        <Grid container spacing={2}>
          {reports.map((report) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={report.key}>
              <Card
                sx={{
                  height: '100%',
                  transition: (theme) => theme.transitions.create(['box-shadow'], { duration: 200 }),
                  '&:hover': { boxShadow: UI.shadowMd }
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                    <StatusUp size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {t(report.title)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(report.description)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button component={Link} to={report.redirectTo ?? `/reports/${report.key}`} size="small" variant="outlined" sx={{ mt: 1.5 }}>
                    {t('report.viewReport')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );
}
